/**
 * SearchNoResults — generic empty state for search form-field dropdowns
 * (Figma 836:15323, Bihar Krishi Web Handoff). An 88px magnifying-glass
 * illustration above a centred message, so every "no matches" panel — the
 * rewamp global search and page-scoped search fields alike (Horti listing, …) —
 * reads identically.
 *
 * It renders only the inner illustration + text; the dropdown chrome (white
 * surface, shadow, bottom radius) is owned by the parent result panel. `message`
 * is an i18n key (or plain string) resolved through t() and defaults to the
 * localized "no results" copy. `role="status"` announces the empty state to
 * assistive tech the moment the panel swaps in.
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import noResultsIllustration from "../../assets/rewamp/search/no-results.svg";

function SearchNoResults({ message, size, sx, icon, textColor }) {
  const { t } = useTranslation();

  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5, // 4px — Figma 836:15324
        p: 2, // 16px — Figma dropdown padding (836:15323)
        textAlign: "center",
        ...sx,
      }}
    >
      <Box
        component="img"
        src={icon}
        alt=""
        aria-hidden="true"
        sx={{ width: size, height: size, flexShrink: 0 }}
      />
      <Typography variant="body2" sx={{ color: textColor }}>
        {t(message)}
      </Typography>
    </Box>
  );
}

SearchNoResults.propTypes = {
  /** i18n key (or literal) for the empty-state message. Defaults to the Figma copy. */
  message: PropTypes.string,
  /** Illustration edge length; a number is treated as px. */
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Extra styles merged onto the wrapper. */
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Illustration asset — override for a caller with its own Figma-specific empty-state art (e.g. scheme search, 639-24516). */
  icon: PropTypes.string,
  /** Theme color token for the message text. */
  textColor: PropTypes.string,
};

SearchNoResults.defaultProps = {
  message: "NO_DATA_FOUND",
  size: 88,
  sx: undefined,
  icon: noResultsIllustration,
  textColor: "text.primary",
};

export default SearchNoResults;
